/** biome-ignore-all lint/a11y/useValidAriaRole: report-table, table-wrapper, etc. used for structure */
/** biome-ignore-all lint/correctness/noUnusedFunctionParameters: Rill context callback signatures */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { DATE_FORMATS } from "../../../constants";
import { usePrevious } from "../../../hooks/usePrevious";
import ButtonSwitch from "../../../parts/buttonSwitch";
import DropdownIcons from "../../../parts/inputs/dropdown-icons";
import TablistNav from "../../../parts/inputs/TablistNav";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { canViewAllOrganizationCoaches } from "../../../utils/user-helpers";
import { OrganizationCoachesPreviewSectionComponent } from "../../dashboard/settings/manage-coaches/OrganizationCoachesPreviewSectionComponent";
import { getUsersAssignableAsCoach } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import CoachingDashboardSkeleton from "./CoachingDashboardSkeleton";
import {
  coachMetricsOptions,
  daysSinceFilterOptions,
  getGroupOptions,
  metricConfig,
  metricsOptions,
  sortItems,
  unitOptions,
} from "./coachingHelpers";
import { DashboardTable } from "./components/DashboardTable";
import { MetricLegend } from "./components/MetricLegend";
import { MonthNavigator } from "./components/MonthNavigator";
import { NoAthletesOverview } from "./components/NoAthletesOverview";
import { submitDaysSince, submitMonth, submitSort } from "./dashboardActions";

const { DATE_YEAR_FORMAT } = DATE_FORMATS;

function CoachingDashboardView() {
  const context = useRillContext();

  const { locals } = context;

  const {
    athletes = [],
    query,
    momentTZ,
    activeMonth,
    today,
    isCurrentMonth,
    isPreviousMonth,
    activeUser,
    coachOrganizationInformation,
    usersOrganization = {
      organizationActiveUsers: [],
    },
    organizationRoleName,
  } = locals;

  const { t } = useTranslation("coaching-portal");
  const theme = locals.activeUser.webAppTheme || "dark";
  const { filterGroup, metric, sort } = query;

  const { organizationActiveUsers = [] } = usersOrganization;
  const athletesGroups = coachOrganizationInformation
    ? coachOrganizationInformation.athletesGroups
    : activeUser.athletesGroups || [];
  const { referAsClients, units } = activeUser;

  const [unitSelected, setUnitSelected] = useState(units);
  const canViewAll = canViewAllOrganizationCoaches(organizationRoleName);
  const clientWord = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");
  // A Team Coach has no personal roster to fall back to, so "all" is already
  // org-wide for them (see coaching-dashboard-controller.js) — the separate
  // "All Organization" tab would be a same-result duplicate.
  const isTeamCoach = organizationRoleName === "Team Coach";
  const groupOptions = getGroupOptions(
    t,
    clientWord,
    athletesGroups,
    canViewAll && !isTeamCoach
  );

  const translatedSortItems = sortItems.map((item) => ({
    ...item,
    title: t(item.titleKey),
    alt: t(item.altKey),
  }));
  const translatedMetricsOptions = metricsOptions.map((item) => ({
    ...item,
    text: t(item.textKey),
  }));
  const translatedCoachMetricsOptions = coachMetricsOptions.map((item) => ({
    ...item,
    text: t(item.textKey),
  }));
  const translatedUnitOptions = Object.fromEntries(
    Object.entries(unitOptions).map(([key, opts]) => [
      key,
      opts.map((opt) => ({ ...opt, name: t(opt.nameKey) })),
    ])
  );

  const updateMonth = (amount) => {
    submitMonth(
      activeMonth.clone().add(amount, "months").format(DATE_YEAR_FORMAT)
    );
  };

  const showWorkoutsColumn = !metric || metric === "workouts";

  const onSortName = () =>
    submitSort(
      sort === JSON.stringify([["name"], ["asc"]])
        ? JSON.stringify([["name"], ["desc"]])
        : JSON.stringify([["name"], ["asc"]])
    );
  const onSortWorkouts = () =>
    submitSort(
      sort === "[[]]" ||
        sort === JSON.stringify([["needsWorkoutsOn"], ["desc"]])
        ? JSON.stringify([["needsWorkoutsOn"], ["asc"]])
        : JSON.stringify([["needsWorkoutsOn"], ["desc"]])
    );
  // none -> highest first -> lowest first -> back to the default order
  const onSortSummary = () =>
    submitSort(
      sort === JSON.stringify([["summary"], ["desc"]])
        ? JSON.stringify([["summary"], ["asc"]])
        : sort === JSON.stringify([["summary"], ["asc"]])
          ? JSON.stringify([["name"], ["asc"]])
          : JSON.stringify([["summary"], ["desc"]])
    );
  const onSortLastLogged = () =>
    submitSort(
      sort === JSON.stringify([["lastLoggedDays"], ["asc"]])
        ? JSON.stringify([["lastLoggedDays"], ["desc"]])
        : JSON.stringify([["lastLoggedDays"], ["asc"]])
    );

  // Clicking the tab that is already active reloads the same view, so
  // `metric`/`filterGroup` never change and the skeleton would never clear.
  const onSelectMetric = (event) => {
    if (event?.currentTarget?.classList?.contains("active")) return;
    setLoading(true);
    setUnitSelected(units);
  };
  const onSelectGroup = (value) => {
    if (value === filterGroup) return;
    setLoading(true);
  };

  const { dataKey } = metricConfig[metric] || metricConfig.workouts;
  const previousMetric = usePrevious(metric);
  const previousFilterGroup = usePrevious(filterGroup);
  const [loading, setLoading] = useState(false);
  // Reset loading when user changes metric or filter; avoid reacting to mount or other deps.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (previousMetric !== metric || previousFilterGroup !== filterGroup)
      setLoading(false);
  }, [metric, filterGroup]);
  return (
    <main className="coaching-dashboard">
      <header>
        <div className="titles">
          <h1>{t("coachingDashboard.title")}</h1>
          <p>{t("coachingDashboard.subtitle")}</p>
        </div>
        <hgroup>
          <DropdownIcons
            title={t("coachingDashboard.sort")}
            items={translatedSortItems}
            action={(sort) => submitSort(sort)}
          />
          <select
            aria-label={t("coachingDashboard.daysSinceFilter.label")}
            className="days-since-filter"
            value={query.daysSince || ""}
            onChange={(e) => submitDaysSince(e.target.value)}
          >
            {daysSinceFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.textKey)}
              </option>
            ))}
          </select>
          {canViewAll && (
            <OrganizationCoachesPreviewSectionComponent
              activeUser={activeUser}
              // A Team Coach never holds clients, so picking it here would
              // always land on an empty dashboard — same reason it's
              // filtered out of every "assign a coach" picker.
              organizationActiveUsers={getUsersAssignableAsCoach(
                organizationActiveUsers
              )}
              query={locals.query}
              isFrom="overview"
            />
          )}
        </hgroup>
      </header>

      {canViewAll && (
        <div role="sub-header" className="flex justify-between gap-4">
          <TablistNav
            name="filterGroup"
            options={groupOptions}
            callback={onSelectGroup}
          />
          {(metric === "bodyWeight" || metric === "bodyMeasurements") && (
            <ButtonSwitch
              options={translatedUnitOptions[metric]}
              selectedOption={translatedUnitOptions[metric].find(
                (opt) => opt.unit === unitSelected
              )}
              size="small"
              onClickFn={(opt) => setUnitSelected(opt.unit)}
            />
          )}
          {metric === "hrv" && (
            <div className="unit-container">
              <button type="button" data-button-type="outline">
                <span>ms</span>
              </button>
            </div>
          )}
        </div>
      )}
      <div className="views">
        <div className="vrow">
          <p className="vlabel">
            {t("coachingDashboard.overviewTitle", { role: clientWord })}
          </p>
          <ToggleLinksInput
            name="metric"
            options={translatedMetricsOptions}
            callback={onSelectMetric}
            className="flex-wrap"
          />
        </div>
        <div className="vrow">
          <p className="vlabel">
            {t("coachingDashboard.coachEngagementOverview")}
          </p>
          <ToggleLinksInput
            name="metric"
            options={translatedCoachMetricsOptions}
            callback={onSelectMetric}
            className="flex-wrap"
          />
        </div>
      </div>

      <div className="toolbar">
        <MetricLegend metric={metric} t={t} />
        <div className="grow" />
        <button
          type="button"
          data-button-type="outline"
          onClick={() => submitMonth(momentTZ().format(DATE_YEAR_FORMAT))}
        >
          <span>{t("coachingDashboard.thisMonth")}</span>
        </button>
        <MonthNavigator activeMonth={activeMonth} onMonthChange={updateMonth} />
      </div>

      {!loading ? (
        <section role="table-wrapper">
          {athletes && athletes.length > 0 ? (
            <DashboardTable
              athletes={athletes}
              dataKey={dataKey}
              theme={theme}
              activeMonth={activeMonth}
              metric={metric}
              today={today}
              isCurrentMonth={isCurrentMonth}
              isPreviousMonth={isPreviousMonth}
              unitSelected={unitSelected}
              activeUser={activeUser}
              organizationActiveUsers={organizationActiveUsers}
              referAsClients={referAsClients}
              showWorkoutsColumn={showWorkoutsColumn}
              onSortName={onSortName}
              onSortWorkouts={onSortWorkouts}
              onSortLastLogged={onSortLastLogged}
              onSortSummary={onSortSummary}
              t={t}
            />
          ) : metric === "menstrualCycle" ? (
            <div className="no-athlete-overview-section">
              <p>{t("coachingDashboard.menstrualCycleNoAthletes")}</p>
            </div>
          ) : (
            <NoAthletesOverview clientWord={clientWord} t={t} />
          )}
        </section>
      ) : (
        <CoachingDashboardSkeleton rows={10} webAppTheme={theme} />
      )}
      <ReactTooltip id="athlete-workoutOn" opacity={1} float />
      <ReactTooltip id="athlete-last-logged-never" opacity={1} float />
      <ReactTooltip id="summary-header-info" opacity={1} />
    </main>
  );
}

export default CoachingDashboardView;
